import * as React from "react";
import {
  ArrowsClockwiseIcon,
  ArrowsSplitIcon,
  ChatCircleIcon,
  CheckIcon,
  ClockIcon,
  FileIcon,
  NotEqualsIcon,
  QuestionIcon,
  SkipForwardIcon,
  TargetIcon,
  WarningIcon,
  type Icon
} from "@phosphor-icons/react";
import { cn } from "cn";

import { isEnterSubmit } from "@libs/zapi-ui/custom";
import { EditableText } from "@libs/zapi-ui/ds/components/editable-text";
import { Progress } from "@libs/zapi-ui/ds/components/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/ds/components/tooltip";
import { prefersReducedMotion } from "@libs/zapi-ui/lib/motion";

type FrameClaimState = "stated" | "conflict" | "missing";

type FrameClaim = {
  id: string;
  text: string;
  state: FrameClaimState;
  // "You" marks a claim the user added.
  source?: string;
  sourceTitle?: string;
  flag?: string;
  note?: string;
  options?: { text: string; source?: string }[];
  toAsk?: boolean;
  label?: string;
  skipped?: boolean;
  issue?: { kind: FrameIssueKind; label: string };
  status?: string;
  sourceAlways?: boolean;
};

type FrameIssueKind = "conflict" | "stale" | "unsupported" | "numbers" | "single-source" | "key" | "updated";

const frameIssueIcons: Record<FrameIssueKind, Icon> = {
  conflict: ArrowsSplitIcon,
  stale: ClockIcon,
  unsupported: QuestionIcon,
  numbers: NotEqualsIcon,
  "single-source": FileIcon,
  key: TargetIcon,
  updated: ArrowsClockwiseIcon
};

type FrameSectionState = "pending" | "writing" | "done";

type FrameSection = {
  id: number;
  label: string;
  hint: string;
  prompt?: string;
  questions?: { id: string; text: string; topic?: string; current?: boolean }[];
  questionsLabel?: string;
  state?: FrameSectionState;
  claims: FrameClaim[];
  // FrameSheet renders it in place of the section's claims, questions and empty text.
  content?: React.ReactNode;
  // Overrides the check FrameSheet would derive from the open claims.
  settled?: boolean;
};

type CaseFrameLabels = {
  askLeo: string;
  skipped: string;
  answerQuestion: (question: string) => string;
  leoWillAsk: string;
  editClaim: (text: string) => string;
  addedByYou: string;
  fromSource: (source: string) => string;
  sourcesDisagree: string;
  yourAnswer: (note: string) => string;
  activeSection: string;
  settledSection: string;
  openCount: (count: number) => string;
  noPrompt: string;
  stillToAnswer: (sectionLabel: string) => string;
  completeness: string;
  sectionsSettled: (settled: number, total: number) => string;
  sectionsWritten: (written: number, total: number) => string;
  percent: (percent: number) => string;
  percentComplete: (percent: number) => string;
  openNext: string;
  toReview: (count: number) => string;
};

const defaultCaseFrameLabels: CaseFrameLabels = {
  askLeo: "Go through this with Leo",
  skipped: "Skipped",
  answerQuestion: (question) => `Answer: ${question}`,
  leoWillAsk: "Leo will ask about this",
  editClaim: (text) => `Edit: ${text}`,
  addedByYou: "Added by you",
  fromSource: (source) => `From ${source}`,
  sourcesDisagree: "Sources disagree",
  yourAnswer: (note) => `Your answer: “${note}”`,
  activeSection: "Leo is asking about this section",
  settledSection: "Settled",
  openCount: (count) => `${count} open`,
  noPrompt: "—",
  stillToAnswer: (sectionLabel) => `Still to answer in ${sectionLabel}`,
  completeness: "Completeness",
  sectionsSettled: (settled, total) => `${settled} of ${total} sections settled`,
  sectionsWritten: (written, total) => `${written} of ${total} sections written`,
  percent: (percent) => `${percent}%`,
  percentComplete: (percent) => `${percent}% complete`,
  openNext: "Go through the next open one with Leo",
  toReview: (count) => `${count} to review`
};

function scrollIntoViewOnMount(node: HTMLLIElement | null) {
  node?.scrollIntoView({ block: "center", behavior: "smooth" });
}

function SourceTag({ source, title }: { source?: string; title?: string }) {
  if (!source) return null;
  return (
    <span title={title} className="ms-2 typo-caption whitespace-nowrap text-muted-foreground">
      {source}
    </span>
  );
}

function IssueChip({
  issue,
  onAsk,
  labels
}: {
  issue: NonNullable<FrameClaim["issue"]>;
  onAsk?: () => void;
  labels: CaseFrameLabels;
}) {
  const IssueIcon = frameIssueIcons[issue.kind];
  const className =
    "ms-2 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 align-[-0.0156rem] typo-label-sm whitespace-nowrap text-primary-text";
  const content = (
    <>
      <IssueIcon weight="bold" className="size-3" aria-hidden="true" />
      {issue.label}
    </>
  );
  if (onAsk)
    return (
      <button
        type="button"
        title={labels.askLeo}
        onClick={(event) => {
          event.stopPropagation();
          onAsk();
        }}
        className={cn(className, "outline-none hover:bg-primary/25 focus-visible:ring-2 focus-visible:ring-ring/50")}
      >
        {content}
      </button>
    );
  return <span className={className}>{content}</span>;
}

function SkippedChip({ onAsk, labels }: { onAsk?: () => void; labels: CaseFrameLabels }) {
  const className =
    "ms-2 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 align-[-0.0156rem] typo-label-sm whitespace-nowrap text-primary-text";
  const content = (
    <>
      <SkipForwardIcon weight="bold" className="size-3" aria-hidden="true" />
      {labels.skipped}
    </>
  );
  if (!onAsk) return <span className={className}>{content}</span>;
  return (
    <button
      type="button"
      title={labels.askLeo}
      onClick={(event) => {
        event.stopPropagation();
        onAsk();
      }}
      className={cn(className, "outline-none hover:bg-primary/25 focus-visible:ring-2 focus-visible:ring-ring/50")}
    >
      {content}
    </button>
  );
}

function StatusChip({ status, onAsk, labels }: { status: string; onAsk?: () => void; labels: CaseFrameLabels }) {
  const className =
    "ms-2 inline-flex items-center rounded-full bg-muted px-2 py-0.5 align-[-0.0156rem] typo-label-sm whitespace-nowrap text-muted-foreground";
  if (onAsk)
    return (
      <button
        type="button"
        title={labels.askLeo}
        onClick={(event) => {
          event.stopPropagation();
          onAsk();
        }}
        className={cn(
          className,
          "outline-none hover:bg-foreground/10 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
        )}
      >
        {status}
      </button>
    );
  return <span className={className}>{status}</span>;
}

function TopicLabel({ topic }: { topic: string }) {
  return <span className="me-1.5 font-medium">{topic}:</span>;
}

function QuestionAnswer({
  question,
  labels,
  onAnswer
}: {
  question: string;
  labels: CaseFrameLabels;
  onAnswer: (text: string) => void;
}) {
  const field = React.useRef<HTMLSpanElement>(null);
  const [empty, setEmpty] = React.useState(true);
  const clear = (element: HTMLSpanElement) => {
    element.textContent = "";
    setEmpty(true);
    element.blur();
  };
  return (
    <span
      className="-mx-1 cursor-text rounded-md px-1 transition-colors hover:bg-foreground/[0.04] has-focus:bg-foreground/[0.05]"
      onClick={() => field.current?.focus()}
    >
      <span
        ref={field}
        role="textbox"
        tabIndex={0}
        aria-label={labels.answerQuestion(question)}
        aria-placeholder={question}
        contentEditable="plaintext-only"
        suppressContentEditableWarning
        spellCheck
        onInput={(event) => {
          const text = event.currentTarget.textContent?.trim() ?? "";
          if (!text) event.currentTarget.textContent = "";
          setEmpty(!text);
        }}
        onBlur={(event) => {
          const text = event.currentTarget.textContent?.trim() ?? "";
          if (!text) return;
          clear(event.currentTarget);
          onAnswer(text);
        }}
        onKeyDown={(event) => {
          if (isEnterSubmit(event)) {
            event.preventDefault();
            event.currentTarget.blur();
          }
          if (event.key === "Escape") clear(event.currentTarget);
        }}
        className="text-foreground outline-none empty:inline-block empty:min-w-px"
      />
      {empty && (
        <span aria-hidden="true" className="text-muted-foreground/70">
          {question}
        </span>
      )}
    </span>
  );
}

function scrollParent(element: HTMLElement) {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);
    if ((overflowY === "auto" || overflowY === "scroll") && parent.scrollHeight > parent.clientHeight) return parent;
  }
  return null;
}

function ToAskTag({ labels }: { labels: CaseFrameLabels }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            role="img"
            aria-label={labels.leoWillAsk}
            className="ms-1.5 inline-flex size-4 items-center justify-center align-[-0.125rem] text-muted-foreground"
          />
        }
      >
        <ChatCircleIcon className="size-3.5" aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>{labels.leoWillAsk}</TooltipContent>
    </Tooltip>
  );
}

function FlagTag({ flag, onAsk, labels }: { flag: string; onAsk?: () => void; labels: CaseFrameLabels }) {
  const content = (
    <>
      <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
      {flag}
    </>
  );
  if (onAsk)
    return (
      <button
        type="button"
        title={labels.askLeo}
        onClick={(event) => {
          event.stopPropagation();
          onAsk();
        }}
        className="ms-2 inline-flex items-center gap-1 rounded-sm align-middle typo-label-sm whitespace-nowrap text-primary-text underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {content}
      </button>
    );
  return (
    <span className="ms-2 inline-flex items-center gap-1 align-middle typo-label-sm whitespace-nowrap text-primary-text">
      {content}
    </span>
  );
}

function leadingWords(label: string) {
  return label.slice(0, label.lastIndexOf(" ") + 1);
}

function lastWord(label: string) {
  return label.slice(label.lastIndexOf(" ") + 1);
}

function FrameClaimRow({
  claim,
  focused,
  onEdit,
  onAsk,
  focusAs = "edge",
  labels
}: {
  claim: FrameClaim;
  focused: boolean;
  focusAs?: "edge" | "card";
  onEdit: (text: string) => void;
  onAsk?: () => void;
  labels: CaseFrameLabels;
}) {
  const row = React.useRef<HTMLLIElement>(null);
  React.useEffect(() => {
    if (focused) row.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focused]);

  return (
    <li
      ref={row}
      data-slot="frame-claim"
      data-state={claim.state}
      data-focused={focused || undefined}
      className={cn(
        "group/claim relative animate-in rounded-lg px-3 py-1.5 transition-colors duration-500 fade-in slide-in-from-bottom-1",
        focusAs === "card"
          ? "my-0.5 -ms-3 ring-1 ring-transparent data-focused:bg-background data-focused:shadow-xs data-focused:ring-foreground/10"
          : "-mx-3 before:absolute before:inset-y-2 before:start-0 before:w-0.5 before:rounded-full before:bg-transparent before:transition-colors data-focused:bg-primary/8 data-focused:before:bg-primary"
      )}
    >
      {claim.skipped ? (
        <p className="typo-body-md text-muted-foreground/70">
          {claim.label && <TopicLabel topic={claim.label} />}
          {claim.text}
          <SkippedChip onAsk={onAsk} labels={labels} />
        </p>
      ) : focusAs === "card" ? (
        (() => {
          const chips = (
            <>
              {claim.issue && <IssueChip issue={claim.issue} onAsk={onAsk} labels={labels} />}
              {claim.status && <StatusChip status={claim.status} labels={labels} />}
              {claim.sourceAlways && <SourceTag source={claim.source} title={claim.sourceTitle} />}
            </>
          );
          const content =
            claim.state === "conflict" ? (
              <p className="typo-body-md text-foreground">
                {claim.label && <TopicLabel topic={claim.label} />}
                {claim.text}
                {chips}
              </p>
            ) : (
              <EditableText
                inline
                className="typo-body-md text-foreground"
                leading={claim.label && <TopicLabel topic={claim.label} />}
                value={claim.text}
                editLabel={labels.editClaim(claim.text.slice(0, 48))}
                onSave={onEdit}
                trailing={chips}
              />
            );
          if (!claim.source || claim.sourceAlways) return content;
          return (
            <Tooltip>
              <TooltipTrigger delay={700} render={<div />}>
                {content}
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="start"
                sideOffset={2}
                className="rounded-md bg-popover px-2 py-1 typo-caption text-muted-foreground shadow-xs ring-1 ring-foreground/10 [&>:last-child]:hidden"
              >
                {claim.source === "You" ? labels.addedByYou : labels.fromSource(claim.sourceTitle ?? claim.source)}
              </TooltipContent>
            </Tooltip>
          );
        })()
      ) : claim.state === "conflict" ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center">
            {onAsk ? (
              <button
                type="button"
                title={labels.askLeo}
                onClick={onAsk}
                className="flex items-center gap-1.5 self-start rounded-sm typo-label-sm text-primary-text underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <WarningIcon weight="bold" className="size-3.5" aria-hidden="true" />
                {claim.flag ?? labels.sourcesDisagree}
              </button>
            ) : (
              <span className="flex items-center gap-1.5 typo-label-sm text-primary-text">
                <WarningIcon weight="bold" className="size-3.5" aria-hidden="true" />
                {claim.flag ?? labels.sourcesDisagree}
              </span>
            )}
            {claim.toAsk && <ToAskTag labels={labels} />}
          </div>
          {claim.options?.map((option) => (
            <p key={option.text} className="typo-body-md text-foreground">
              {claim.label && <TopicLabel topic={claim.label} />}
              {option.text}
              <SourceTag source={option.source} />
            </p>
          ))}
        </div>
      ) : claim.state === "missing" ? (
        <p className="rounded-md border border-dashed border-primary-400 px-3 py-2 typo-body-md text-muted-foreground">
          {claim.label && <TopicLabel topic={claim.label} />}
          {claim.text}
          {claim.flag && <FlagTag flag={claim.flag} onAsk={onAsk} labels={labels} />}
          {claim.toAsk && <ToAskTag labels={labels} />}
        </p>
      ) : (
        <EditableText
          className="typo-body-md text-foreground"
          leading={claim.label && <TopicLabel topic={claim.label} />}
          value={claim.text}
          editLabel={labels.editClaim(claim.text.slice(0, 48))}
          onSave={onEdit}
          trailing={
            <>
              <SourceTag source={claim.source} title={claim.sourceTitle} />
              {claim.flag && <FlagTag flag={claim.flag} onAsk={onAsk} labels={labels} />}
              {claim.toAsk && <ToAskTag labels={labels} />}
            </>
          }
        />
      )}
      {claim.note && <p className="mt-1 typo-caption text-muted-foreground">{labels.yourAnswer(claim.note)}</p>}
    </li>
  );
}

// Hue 25 (red) while little is settled, through amber, to 150 (green) when all of it is.
function completenessColor(percent: number) {
  return `oklch(0.7 0.17 ${25 + percent * 1.25})`;
}

function FrameCompleteness({
  sections,
  awaitingId,
  settled,
  attention = [],
  activeId,
  openCount = 0,
  onOpenNext,
  percent: givenPercent,
  labels: labelOverrides,
  className
}: {
  sections: FrameSection[];
  awaitingId?: number;
  settled?: number[];
  attention?: number[];
  activeId?: number;
  openCount?: number;
  onOpenNext?: () => void;
  percent?: number;
  labels?: Partial<CaseFrameLabels>;
  className?: string;
}) {
  const labels = { ...defaultCaseFrameLabels, ...labelOverrides };
  const written = settled
    ? settled.length
    : sections.filter((section) => (section.state ?? "done") === "done" && section.id !== awaitingId).length;
  const percent = givenPercent ?? (sections.length === 0 ? 0 : Math.round((written / sections.length) * 100));
  const toReview = settled
    ? 0
    : sections.flatMap((section) => section.claims).filter((claim) => claim.flag || claim.state !== "stated").length;
  const segments = (
    <ol
      className={cn("grid gap-1", settled && "w-24 shrink-0")}
      style={{
        gridTemplateColumns: `repeat(${Math.max(sections.length, 1)}, minmax(0, 1fr))`
      }}
      aria-label={
        settled ? labels.sectionsSettled(written, sections.length) : labels.sectionsWritten(written, sections.length)
      }
    >
      {sections.map((section) => {
        const state = section.state ?? "done";
        return (
          <li
            key={section.id}
            title={section.label}
            className={cn(
              "h-1 rounded-full transition-colors duration-500",
              settled
                ? settled.includes(section.id)
                  ? "bg-primary"
                  : attention.includes(section.id)
                  ? "bg-primary/40"
                  : section.id === activeId
                  ? "animate-pulse bg-foreground/20"
                  : "bg-muted"
                : section.id === awaitingId
                ? "bg-primary/40"
                : state === "done"
                ? "bg-primary"
                : state === "writing"
                ? "animate-pulse bg-primary/40"
                : "bg-muted"
            )}
          />
        );
      })}
    </ol>
  );
  if (settled)
    return (
      <div data-slot="frame-completeness" className={cn("flex items-center gap-3", className)}>
        <span className="typo-body-sm whitespace-nowrap text-muted-foreground">{labels.completeness}</span>
        <Progress
          value={percent}
          aria-label={
            givenPercent === undefined
              ? labels.sectionsSettled(written, sections.length)
              : labels.percentComplete(percent)
          }
          className="w-28 shrink-0 [&_[data-slot=progress-indicator]]:bg-(--completeness-color)"
          style={
            {
              "--completeness-color": completenessColor(percent)
            } as React.CSSProperties
          }
        />
        <span className="typo-label-md whitespace-nowrap text-foreground tabular-nums">{labels.percent(percent)}</span>
        {openCount > 0 && (
          <button
            type="button"
            onClick={onOpenNext}
            disabled={!onOpenNext}
            title={labels.openNext}
            className="inline-flex items-center gap-1 rounded-sm typo-caption whitespace-nowrap text-primary-text underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none"
          >
            <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
            {labels.openCount(openCount)}
          </button>
        )}
      </div>
    );

  return (
    <div data-slot="frame-completeness" className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="typo-overline text-muted-foreground">{labels.completeness}</span>
        <span className="flex items-baseline gap-2 typo-label-sm text-foreground tabular-nums">
          {toReview > 0 && (
            <span className="inline-flex items-center gap-1 typo-caption text-primary-text">
              <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
              {labels.toReview(toReview)}
            </span>
          )}
          {labels.percent(percent)}
        </span>
      </div>
      {segments}
    </div>
  );
}

function FrameSheet({
  sections,
  focusId,
  activeId,
  openCounts = {},
  emptyText,
  onEditClaim,
  onAskClaim,
  onAnswerQuestion,
  labels: labelOverrides,
  className
}: {
  sections: FrameSection[];
  focusId?: string | null;
  activeId?: number;
  openCounts?: Partial<Record<number, number>>;
  emptyText: string;
  onEditClaim: (claimId: string, text: string) => void;
  onAskClaim?: (claimId: string) => void;
  onAnswerQuestion?: (questionId: string, text: string) => void;
  labels?: Partial<CaseFrameLabels>;
  className?: string;
}) {
  const labels = { ...defaultCaseFrameLabels, ...labelOverrides };
  const writingId = activeId ?? sections.find((section) => section.state === "writing")?.id;
  const root = React.useRef<HTMLOListElement>(null);
  // The section Leo moves to comes to the top first, so its claims are written in view.
  React.useEffect(() => {
    if (writingId === undefined) return;
    const section = root.current?.querySelector<HTMLElement>(`#frame-sheet-${writingId}`);
    const scroller = section && scrollParent(section);
    if (!section || !scroller) return;
    scroller.scrollTo({
      top: section.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 8,
      behavior: prefersReducedMotion() ? "auto" : "smooth"
    });
  }, [writingId]);

  return (
    <ol
      ref={root}
      data-slot="frame-sheet"
      className={cn(
        "-mx-3 flex flex-col divide-y divide-border [&>[data-active]]:border-transparent [&>li:has(+[data-active])]:border-transparent",
        className
      )}
    >
      {sections.map((section) => {
        const state = section.state ?? "done";
        const active = section.id === activeId;
        const open = openCounts[section.id] ?? 0;
        const settled = !active && state === "done" && (section.settled ?? open === 0);
        const questions = section.questions ?? [];
        return (
          <li
            key={section.id}
            id={`frame-sheet-${section.id}`}
            data-state={state}
            data-active={active || undefined}
            className="group/section grid scroll-mt-2 gap-x-6 gap-y-1 px-3 py-3 transition-colors duration-500 sm:grid-cols-[11rem_minmax(0,1fr)] data-active:rounded-xl data-active:bg-muted/60"
          >
            <div className="flex items-start gap-2.5 self-start sm:pt-2.5">
              <span className="flex h-5 items-center">
                <span
                  aria-label={active ? labels.activeSection : settled ? labels.settledSection : undefined}
                  className={cn(
                    "flex size-5.5 shrink-0 items-center justify-center rounded-full typo-label-sm tabular-nums transition-colors duration-300",
                    active
                      ? "bg-primary text-primary-foreground"
                      : settled
                      ? "bg-success/12 text-success"
                      : state === "done"
                      ? "bg-primary/15 text-primary-text"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {settled ? <CheckIcon weight="bold" className="size-3" aria-hidden="true" /> : section.id}
                </span>
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="typo-label-md text-foreground transition-colors group-data-[state=pending]/section:text-muted-foreground">
                  {leadingWords(section.label)}
                  <span className="whitespace-nowrap">{lastWord(section.label)}</span>
                </span>
                {!active && state === "done" && open > 0 && (
                  <span className="typo-caption text-primary-text tabular-nums">{labels.openCount(open)}</span>
                )}
              </span>
            </div>
            <div className="min-w-0">
              {section.content !== undefined ? (
                section.content
              ) : (
                <>
                  {section.claims.length > 0 ? (
                    <ul className="flex flex-col">
                      {section.claims.map((claim) => (
                        <FrameClaimRow
                          key={claim.id}
                          claim={claim}
                          focused={claim.id === focusId}
                          focusAs="card"
                          labels={labels}
                          onEdit={(text) => onEditClaim(claim.id, text)}
                          onAsk={
                            onAskClaim && (claim.flag || claim.issue || claim.skipped || claim.state !== "stated")
                              ? () => onAskClaim(claim.id)
                              : undefined
                          }
                        />
                      ))}
                    </ul>
                  ) : questions.length > 0 ? null : state === "writing" ? (
                    <div className="flex flex-col gap-2 py-2.5" aria-hidden="true">
                      <span className="h-3 w-4/5 animate-pulse rounded-full bg-muted" />
                      <span className="h-3 w-3/5 animate-pulse rounded-full bg-muted" />
                    </div>
                  ) : (
                    <p className="py-2 typo-body-md text-muted-foreground/70">
                      {state === "done" ? emptyText : section.prompt ?? labels.noPrompt}
                    </p>
                  )}
                  {questions.length > 0 && section.questionsLabel && (
                    <p
                      className={cn(
                        "typo-caption text-muted-foreground",
                        section.claims.length > 0 ? "pt-3" : "pt-1.5"
                      )}
                    >
                      {section.questionsLabel}
                    </p>
                  )}
                  {questions.length > 0 && (
                    <ol
                      aria-label={labels.stillToAnswer(section.label)}
                      className={cn(
                        "flex flex-col",
                        section.questionsLabel ? "pt-1 pb-0.5" : section.claims.length > 0 ? "pt-1.5" : "pb-1"
                      )}
                    >
                      {questions.map((question) => (
                        <li
                          key={question.id}
                          ref={question.current ? scrollIntoViewOnMount : undefined}
                          aria-current={question.current ? "step" : undefined}
                          className={cn(
                            "my-0.5 -ms-3 rounded-lg px-3 py-1.5 typo-body-md ring-1 ring-transparent transition-colors duration-300",
                            question.current
                              ? "bg-background text-foreground shadow-xs ring-foreground/10"
                              : "text-muted-foreground/70"
                          )}
                        >
                          {question.topic && <TopicLabel topic={question.topic} />}
                          {question.current && onAnswerQuestion ? (
                            <QuestionAnswer
                              question={question.text}
                              labels={labels}
                              onAnswer={(text) => onAnswerQuestion(question.id, text)}
                            />
                          ) : (
                            <span className={cn(question.current && "text-muted-foreground/70")}>{question.text}</span>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export {
  frameIssueIcons,
  defaultCaseFrameLabels,
  FrameCompleteness,
  FrameSheet,
  type CaseFrameLabels,
  type FrameIssueKind,
  type FrameClaim,
  type FrameClaimState,
  type FrameSection,
  type FrameSectionState
};
